/**
 * Générateur — Automatisme : prendre une fraction d'une quantité
 * Attendu ID : 1014 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait calculer 2/3 de
 * 12 œufs, 3/4 de 10 m. »
 * Labels (cartes exerciseur) :
 *   - Fraction d'une quantité
 *
 * ── Plan de cartes (2026-08-21, validé prof) ───────────────────────────────
 * Mécanisme rituel : « Le dénominateur dit en combien je coupe. Le numérateur
 * dit combien j'en prends. » (couper → part ; prélever → résultat).
 * BORNES (cadrage prof) :
 *   - dénominateur d ∈ {2, 3, 4, 5, 6} (la division reste à diviseur ≤ 6) ;
 *   - numérateur n < d, fraction IRRÉDUCTIBLE (jamais « 2/4 de 12 ») ;
 *   - unitaires (n = 1, le « temps 1 » du CM2) ≈ 1/3 des tirages (partout
 *     sauf QCM : pour 1/d l'erreur cible « diviser par le numérateur »
 *     n'existe pas) ;
 *   - quantité Q = d × q, q ∈ [2..10] (quotient dans les tables), Q ≤ 60 ;
 *   - cas CONTINU officiel (« 3/4 de 10 m = 7,5 m ») admis ≈ 1/5 des tirages
 *     des formats mécanisme et barre : d ∈ {2, 4}, part en « x,5 », unité
 *     continue (m ou L) obligatoire ;
 *   - contextes légers (œufs, billes, jetons) en appoint sur mécanisme et
 *     barre, le reste sans unité (comme la série flash).
 * Tout est recalculé en DEMI-UNITÉS entières (×2) : aucune arithmétique
 * flottante, gardes qui throwent sur chaque borne.
 * ERREUR CIBLE (cadrage) : diviser par le numérateur (2/3 de 12 → 6) ;
 * familles QCM/VF nommées, jamais de distracteur au hasard.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeSaisie, makeClasser,
} from '../../../utils';

const ATTENDU_ID = 1014;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Fraction d’une quantité';

const COLORIE = '#60a5fa';
const VIDE = '#ffffff';
const NOIR = '#111827';

// ─── Domaine et outils ────────────────────────────────────────────────────

const pgcd = (x, y) => (y === 0 ? x : pgcd(y, x % y));

/** Paires (n, d) irréductibles avec n ≥ 2 (pour le QCM et les mélanges). */
const PAIRES_NON_UNITAIRES = [[2, 3], [3, 4], [2, 5], [3, 5], [4, 5], [5, 6]];

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1014 : ${message}`);
};

/** Format français d'une valeur en demi-unités (15 → « 7,5 », 16 → « 8 »). */
const fmt2 = (v2) => (v2 % 2 === 0 ? String(v2 / 2) : `${(v2 - 1) / 2},5`);

/**
 * Tire un item (n, d, Q) et calcule part et résultat en demi-unités.
 * options : { unitaires: bool (n = 1 admis, ~1/3), demi: bool (cas continu
 * « x,5 » admis, ~1/5), contexte: bool (unité discrète en appoint) }
 */
const tirerItem = ({ unitaires = true, demi = false, contexte = false } = {}) => {
    const casDemi = demi && Math.random() < 0.2;
    let n; let d; let Q;
    if (casDemi) {
        // Restriction prof 2026-08-21 : le cheminement doit passer par les
        // tables. d = 2 : moitié d'un IMPAIR ≤ 19 (« 17 ÷ 2 : 16 ÷ 2 = 8,
        // reste 1 → 8,5 ») ; d = 4 : UNIQUEMENT Q = 10, le cas canonique du
        // programme (3/4 de 10 m = 7,5). Jamais de 18 ÷ 4 ni de 38 ÷ 4.
        d = randomChoice([2, 4]);
        if (d === 2) {
            n = 1;
            Q = 2 * randInt(2, 9) + 1;            // 5, 7, ..., 19
        } else {
            n = unitaires && Math.random() < 1 / 3 ? 1 : 3;
            Q = 10;
        }
    } else {
        if (unitaires && Math.random() < 1 / 3) {
            d = randomChoice([2, 3, 4, 5, 6]);
            n = 1;
        } else {
            [n, d] = randomChoice(PAIRES_NON_UNITAIRES);
        }
        Q = d * randInt(2, 10);
    }
    garde(d >= 2 && d <= 6, 'dénominateur hors {2..6}');
    garde(n >= 1 && n < d && pgcd(n, d) === 1, 'fraction non irréductible ou n ≥ d');
    garde(Q <= 60 && (2 * Q) % d === 0, 'quantité hors bornes');
    const q2 = (2 * Q) / d;                       // la part, en demi-unités
    const r2 = n * q2;                            // le résultat, en demi-unités
    garde(casDemi ? q2 % 2 === 1 : q2 % 2 === 0, 'part incohérente avec le régime');
    garde(q2 >= 4 && q2 <= 21, 'part hors tables');
    let unite = '';
    if (casDemi) unite = randomChoice([' m', ' L']);
    else if (contexte && Math.random() < 1 / 3) unite = randomChoice([' œufs', ' billes', ' jetons']);
    return { n, d, Q, q2, r2, unite, casDemi };
};

const fracStr = (n, d) => `$\\dfrac{${n}}{${d}}$`;

// ─── Format 1 : la phrase-mécanisme (texte à trous, saisie) ───────────────

const genMecanismeTrouSaisie = () => {
    const it = tirerItem({ unitaires: true, demi: true, contexte: true });
    const texte = `<p><b>Calcule ${fracStr(it.n, it.d)} de ${it.Q}${it.unite}.</b></p>`;
    // Le mécanisme rituel, dans l'ordre : couper puis prélever. Trous ancrés
    // par l'équation (3 × {{part}} = {{résultat}} : aucun échange n'est vrai).
    // Division en MOTS (« je partage ... en ... parts »), jamais le symbole ÷
    // qui n'est pas d'usage dans l'enseignement français (prof 2026-08-21).
    // Unitaire : phrase-réponse qui reboucle sur la question (nom de la
    // fraction en toutes lettres), les deux trous portent la MÊME valeur.
    const NOMS_UNITAIRES = { 2: 'la moitié', 3: 'un tiers', 4: 'un quart', 5: 'un cinquième', 6: 'un sixième' };
    const phrase = it.n === 1
        ? `Je partage ${it.Q} en ${it.d} parts : chaque part vaut {{${fmt2(it.q2)}}} ; ${NOMS_UNITAIRES[it.d]} de ${it.Q}, c'est {{${fmt2(it.q2)}}}`
        : `Je partage ${it.Q} en ${it.d} parts : chaque part vaut {{${fmt2(it.q2)}}} ; j'en prends ${it.n} : ${it.n} × {{${fmt2(it.q2)}}} = {{${fmt2(it.r2)}}}`;
    return makeTexteTrou(phrase, texte, { ...opts(L1), mode: 'saisie' });
};

// ─── Format 2 : QCM à erreurs nommées (entiers, n ≥ 2) ────────────────────

const genQcm = () => {
    // Deux régimes (décision prof 2026-08-21) : 2/3 des tirages avec Q
    // divisible par n → L'ERREUR CIBLE ouvre toujours la grille ; 1/3 avec Q
    // non divisible (le domaine s'élargit) → trio de secours part seule /
    // complément / une part de trop, « multiplié » en repli de collision.
    const regimeCible = Math.random() < 2 / 3;
    for (let essai = 0; essai < 80; essai++) {
        const it = tirerItem({ unitaires: false, demi: false });
        if (regimeCible !== (it.Q % it.n === 0)) continue;
        const r = it.r2 / 2;
        const q = it.q2 / 2;
        const candidats = [];
        if (it.Q % it.n === 0) {
            candidats.push({ valeur: it.Q / it.n, code: 'divise_par_numerateur' });
            candidats.push({ valeur: (it.Q / it.n) * it.d, code: 'echange_roles' });
        }
        candidats.push({ valeur: q, code: 'part_unitaire' });
        candidats.push({ valeur: it.Q - r, code: 'complementaire' });
        candidats.push({ valeur: (it.n + 1) * q, code: 'part_de_trop' });
        candidats.push({ valeur: it.Q * it.n, code: 'multiplie_par_numerateur' });
        // dédoublonnage numérique, ordre de priorité ci-dessus ; en régime
        // cible, l'erreur cible est en tête donc toujours retenue
        const vus = new Set([r]);
        const dis = candidats.filter((c) => {
            if (vus.has(c.valeur) || c.valeur === r) return false;
            vus.add(c.valeur);
            return true;
        }).slice(0, 3);
        if (dis.length < 3) continue;             // on RETIRE un tirage complet
        if (regimeCible && dis[0].code !== 'divise_par_numerateur') continue;
        const payload = makeQcm(
            [String(r), ...dis.map((c) => String(c.valeur))],
            String(r),
            `<p><b>Combien vaut ${fracStr(it.n, it.d)} de ${it.Q} ?</b></p>`,
            opts(L1),
            false,
        );
        payload._meta.params = { ...it, distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(['8', '6', '4', '16'], '8',
        `<p><b>Combien vaut ${fracStr(2, 3)} de 12 ?</b></p>`, opts(L1), false);
    payload._meta.params = { n: 2, d: 3, Q: 12, distracteurs: [] };
    return payload;
};

// ─── Format 3 : tableau Vrai/Faux (entiers) ───────────────────────────────

const genVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const lignes = [];
        const verites = [];
        const vus = new Set();
        for (let t = 0; t < 60 && lignes.length < 4; t++) {
            const it = tirerItem({ unitaires: true, demi: false });
            const cle = `${it.n}/${it.d}/${it.Q}`;
            if (vus.has(cle)) continue;
            const r = it.r2 / 2;
            const q = it.q2 / 2;
            let X;
            if (Math.random() < 0.5) X = r;
            else {
                // erreurs NOMMÉES : diviser par le numérateur, oublier de
                // prélever, prendre le complément — jamais de hasard
                const faux = [];
                if (it.n >= 2 && it.Q % it.n === 0 && it.Q / it.n !== r) faux.push(it.Q / it.n);
                if (it.n >= 2 && q !== r) faux.push(q);
                if (it.Q - r !== r) faux.push(it.Q - r);
                if (faux.length === 0) continue;
                X = randomChoice(faux);
            }
            // Vérité RE-CALCULÉE (jamais l'intention) :
            const verite = it.n * it.Q === X * it.d;
            vus.add(cle);
            lignes.push(`${fracStr(it.n, it.d)} de ${it.Q} = ${X}`);
            verites.push(verite);
        }
        const nbVraies = verites.filter(Boolean).length;
        if (lignes.length !== 4 || nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [`${fracStr(2, 3)} de 12 = 8`, `${fracStr(3, 4)} de 20 = 5`,
            `${fracStr(1, 2)} de 18 = 9`, `${fracStr(3, 5)} de 20 = 10`],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 4 : le schéma en barre (saisie sur figure) ────────────────────

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

/** Barre étiquetée Q, d segments à traits noirs gras, n coloriés à gauche
 *  (contigus : on PRÉLÈVE les premières parts, c'est le geste). */
const barreSvg = (n, d, Q, unite) => {
    garde(n >= 1 && n < d && d >= 2 && d <= 6, 'barre : fraction hors domaine');
    const w = 300 / d;
    const cases = Array.from({ length: d }, (_, k) => {
        const fill = k < n ? COLORIE : VIDE;
        return `<rect x="${(10 + k * w).toFixed(2)}" y="30" width="${w.toFixed(2)}" height="40" fill="${fill}" stroke="${NOIR}" stroke-width="2"/>`;
    }).join('');
    const accolade = `<path d="M10 24 v-6 h300 v6" fill="none" stroke="${NOIR}" stroke-width="1.6"/>`
        + `<rect x="140" y="2" width="40" height="18" fill="${VIDE}"/>`
        + `<text x="160" y="16" font-size="14" font-weight="700" fill="${NOIR}" text-anchor="middle" font-family="system-ui,sans-serif">${Q}${unite}</text>`;
    return dataUrl(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 80" width="320" role="img" aria-label="Une barre représentant ${Q}${unite}, partagée en ${d} parts égales, ${n} coloriées">${accolade}${cases}</svg>`);
};

const genBarreSaisie = () => {
    const it = tirerItem({ unitaires: true, demi: true, contexte: true });
    const texte = `<p><b>La barre entière représente ${it.Q}${it.unite}. Quelle quantité représentent les parts coloriées ?</b></p>{{element:0}}`;
    return makeSaisie(fmt2(it.r2), texte, {
        ...opts(L1),
        suffixe: it.unite.trim(),
        elements_additionnels: elImage(barreSvg(it.n, it.d, it.Q, it.unite)),
    });
};

// ─── Format 5 : classer par valeur (entiers) ──────────────────────────────

/** Toutes les écritures (n, d, Q) entières de valeur v (bornes du domaine). */
const ecrituresDeValeur = (v) => {
    const res = [];
    for (const d of [2, 3, 4, 5, 6]) {
        for (let n = 1; n < d; n++) {
            if (pgcd(n, d) !== 1) continue;
            if (v % n !== 0) continue;
            const q = v / n;
            if (q < 2 || q > 10) continue;
            res.push({ n, d, Q: d * q });
        }
    }
    return res;
};

const genClasser = () => {
    for (let essai = 0; essai < 60; essai++) {
        const valeurs = shuffle([6, 8, 9, 10, 12, 15, 20]).slice(0, 2);
        const groupes = valeurs.map((v) => ({
            v,
            ecritures: shuffle(ecrituresDeValeur(v)).slice(0, randInt(2, 3)),
        }));
        if (groupes.some((g) => g.ecritures.length < 2)) continue;
        const categories = groupes.map((g) => ({
            name: `= ${g.v}`,
            items: g.ecritures.map((e) => `${fracStr(e.n, e.d)} de ${e.Q}`),
        }));
        const tous = categories.flatMap((c) => c.items);
        if (new Set(tous).size !== tous.length) continue;
        // chaque écriture est VÉRIFIÉE : sa valeur désigne une seule catégorie
        for (const g of groupes) {
            g.ecritures.forEach((e) => garde(e.n * e.Q === g.v * e.d, 'classer : valeur incohérente'));
        }
        return makeClasser(
            categories,
            '<p><b>Range chaque calcul dans la bonne colonne.</b></p>',
            opts(L1),
        );
    }
    return makeClasser(
        [
            { name: '= 6', items: [`${fracStr(1, 2)} de 12`, `${fracStr(2, 3)} de 9`] },
            { name: '= 8', items: [`${fracStr(2, 3)} de 12`, `${fracStr(1, 3)} de 24`] },
        ],
        '<p><b>Range chaque calcul dans la bonne colonne.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'd ∈ {2..6}, n < d irréductible, Q = d × q avec q ∈ [2..10], Q ≤ 60 — gardes qui throwent',
        'cas continu « x,5 » ≈ 1/5 des tirages mécanisme/barre uniquement, unité m ou L',
        'arithmétique en demi-unités entières (×2), jamais de flottant',
    ],
    fracquant_qcm_unique: {
        support: 'n ≥ 2 (pour 1/d, l\'erreur cible n\'existe pas), quantités entières ; 2 régimes : Q divisible par n (2/3, l\'erreur cible ouvre TOUJOURS la grille), Q non divisible (1/3, trio de secours)',
        fixes: {
            divise_par_numerateur: 'Q ÷ n : L\'ERREUR CIBLE du cadrage (2/3 de 12 → 6) — régime divisible uniquement',
            echange_roles: '(Q ÷ n) × d : rôles échangés — régime divisible uniquement',
            part_unitaire: 'Q ÷ d : la part, on a oublié de prélever',
            complementaire: 'Q − résultat : on a pris ce qui reste',
            part_de_trop: '(n + 1) × part : une part prélevée de trop',
            multiplie_par_numerateur: 'Q × n : multiplié au lieu de couper (repli)',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    fracquant_mecanisme_texte_trou_saisie: genMecanismeTrouSaisie,
    fracquant_qcm_unique: genQcm,
    fracquant_tableau_vf: genVraiFaux,
    fracquant_barre_saisie: genBarreSaisie,
    fracquant_classer: genClasser,
};

const FORMAT_LABELS = {
    mecanisme_texte_trou_saisie: 'Mécanisme',
    qcm_unique: 'QCM',
    tableau_vf: 'Vrai/Faux',
    barre_saisie: 'Barre',
    classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'fracquant', label: L1, formatsList: buildFormats('fracquant') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
